import { useEffect, useRef, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Chart } from 'primereact/chart';
import ReportService from '../../services/ReportService';

const currency = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' });
const fmtMoney = (v) => currency.format(v ?? 0);

const toLocalYMD = (d) => {
    if (!(d instanceof Date)) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

export default function BranchReportPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([new Date(new Date().getFullYear(), new Date().getMonth(), 1), new Date()]);
    const [chartData, setChartData] = useState({});
    
    const toast = useRef(null);
    const dt = useRef(null);

    const fetchReport = async () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return;
        setLoading(true);
        try {
            const res = await ReportService.getBranchReport(toLocalYMD(dateRange[0]), toLocalYMD(dateRange[1]));
            const reportData = res.data.data ?? [];
            setData(reportData);

            setChartData({
                labels: reportData.map(r => r.branchName),
                datasets: [
                    {
                        label: 'Satışlar',
                        backgroundColor: '#10b981', // emerald
                        borderRadius: 6,
                        data: reportData.map(r => r.saleTotal)
                    },
                    {
                        label: 'Alışlar',
                        backgroundColor: '#3b82f6', // blue
                        borderRadius: 6,
                        data: reportData.map(r => r.purchaseTotal)
                    },
                    {
                        label: 'Tahsilatlar',
                        backgroundColor: '#8b5cf6', // violet
                        borderRadius: 6,
                        data: reportData.map(r => r.collectionTotal)
                    },
                    {
                        label: 'Giderler',
                        backgroundColor: '#ef4444', // red
                        borderRadius: 6,
                        data: reportData.map(r => r.expenseTotal)
                    }
                ]
            });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Şube raporu alınamadı' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const totals = {
        sales: data.reduce((acc, r) => acc + (r.saleTotal || 0), 0),
        purchases: data.reduce((acc, r) => acc + (r.purchaseTotal || 0), 0),
        collections: data.reduce((acc, r) => acc + (r.collectionTotal || 0), 0),
        expenses: data.reduce((acc, r) => acc + (r.expenseTotal || 0), 0)
    };

    const header = (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 py-2 px-4">
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Tarih Aralığı</span>
                    <Calendar value={dateRange} onChange={(e) => setDateRange(e.value)} selectionMode="range" readOnlyInput dateFormat="dd.mm.yy" className="w-full sm:w-64" showIcon inputClassName="font-bold text-slate-700 py-2" />
                </div>
                <Button icon="pi pi-search" label="SORGULA" onClick={fetchReport} loading={loading} className="!bg-blue-600 border-none hover:!bg-blue-700 px-6 h-[40px] rounded-xl font-black text-[11px] shadow-lg shadow-blue-600/20 text-white mt-5" />
            </div>
            <div className="flex items-center gap-3 mt-2 sm:mt-5">
                <Button type="button" icon="pi pi-file-excel" label="EXCEL" severity="success" onClick={exportCSV} className="!bg-emerald-600 border-none rounded-xl px-4 h-[40px] font-black text-[11px] text-white" />
            </div>
        </div>
    );

    const chartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 0.8,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    font: { size: 11, weight: 'bold' }
                }
            },
            tooltip: {
                padding: 12,
                backgroundColor: '#1e293b',
                titleFont: { size: 13, weight: 'bold' },
                bodyFont: { size: 12 }
            }
        },
        scales: {
            x: {
                grid: { display: false }
            },
            y: {
                beginAtZero: true,
                grid: { color: '#f1f5f9' }
            }
        }
    };

    return (
        <div className="p-2 md:p-4 bg-[#f8fafc] min-h-screen animate-fade-in">
            <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

            {/* Elite Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-6">
                <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-[2rem] !bg-blue-600 flex items-center justify-center shadow-2xl shadow-blue-200">
                        <i className="pi pi-map-marker text-white text-3xl" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight tracking-tighter uppercase">Şube Raporu</h1>
                        <div className="flex items-center gap-3 mt-1">
                            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                            <p className="text-slate-500 font-bold text-sm uppercase tracking-widest italic">Şubeler Arası Performans Karşılaştırma</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                {[
                    { label: "Toplam Satış (Ciro)", value: totals.sales, color: "text-emerald-600", bg: "bg-emerald-50", icon: "pi-shopping-cart" },
                    { label: "Toplam Alış", value: totals.purchases, color: "text-blue-600", bg: "bg-blue-50", icon: "pi-download" },
                    { label: "Toplam Tahsilat", value: totals.collections, color: "text-violet-600", bg: "bg-violet-50", icon: "pi-check-circle" },
                    { label: "Toplam Gider", value: totals.expenses, color: "text-rose-600", bg: "bg-rose-50", icon: "pi-wallet" }
                ].map((s, i) => (
                    <div key={i} className={`${s.bg} p-4 rounded-[2.5rem] border border-white/50 shadow-xl flex items-center justify-between transition-all hover:scale-[1.02]`}>
                        <div>
                            <p className={`text-[10px] font-black ${s.color} uppercase tracking-[0.2em] mb-1`}>{s.label}</p>
                            <h3 className={`text-2xl font-black ${s.color} tracking-tighter`}>{fmtMoney(s.value)}</h3>
                        </div>
                        <div className={`w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center ${s.color}`}>
                            <i className={`pi ${s.icon} text-xl`} />
                        </div>
                    </div>
                ))}
            </div>

            {/* Chart and Table Section */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Chart Card */}
                <div className="lg:col-span-5">
                    <div className="bg-white shadow-2xl shadow-slate-200/50 border border-slate-200 rounded-[2.5rem] p-5 h-full">
                        <h3 className="font-black text-slate-800 uppercase tracking-tighter text-lg mb-6 flex items-center gap-2">
                            <i className="pi pi-chart-bar text-blue-500" />
                            Performans Kıyaslama Grafiği
                        </h3>
                        <div className="h-[400px]">
                            {data.length > 0 ? (
                                <Chart type="bar" data={chartData} options={chartOptions} className="w-full h-full" />
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                                    <i className="pi pi-chart-bar text-4xl mb-2" />
                                    <span>Veri bulunamadı</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="lg:col-span-7">
                    <div className="bg-white shadow-2xl shadow-slate-200/50 border border-slate-200 rounded-[2.5rem] overflow-hidden">
                        <div className="p-2">
                            <DataTable
                                ref={dt}
                                value={data}
                                loading={loading}
                                header={header}
                                className="p-datatable-sm"
                                emptyMessage="Kayıt bulunamadı."
                                paginator
                                rows={10}
                                rowsPerPageOptions={[10, 25, 50]}
                                rowClassName={() => "hover:bg-slate-50/80 transition-colors"}
                            >
                                <Column field="branchName" header="ŞUBE ADI" body={(r) => <span className="font-black text-slate-700">{r.branchName}</span>} sortable headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3" />
                                
                                <Column field="saleTotal" header="SATIS" body={(r) => <span className="text-emerald-600 font-bold">{fmtMoney(r.saleTotal)}</span>} sortable style={{ textAlign: 'right' }} headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 text-right" />
                                
                                <Column field="purchaseTotal" header="ALIS" body={(r) => <span className="text-blue-600 font-bold">{fmtMoney(r.purchaseTotal)}</span>} sortable style={{ textAlign: 'right' }} headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 text-right" />
                                
                                <Column header="NET FARK" body={(r) => {
                                    const net = (r.saleTotal || 0) - (r.purchaseTotal || 0);
                                    return <span className={`font-black ${net >= 0 ? 'text-emerald-600 bg-emerald-50' : 'text-rose-600 bg-rose-50'} px-2 py-0.5 rounded`}>{fmtMoney(net)}</span>
                                }} sortable style={{ textAlign: 'right' }} headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 text-right" />

                                <Column field="collectionTotal" header="TAHSİLAT" body={(r) => <span className="text-violet-600 font-bold">{fmtMoney(r.collectionTotal)}</span>} sortable style={{ textAlign: 'right' }} headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 text-right" />
                                
                                <Column field="expenseTotal" header="GİDER" body={(r) => <span className="text-rose-600 font-bold">{fmtMoney(r.expenseTotal)}</span>} sortable style={{ textAlign: 'right' }} headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 text-right" />

                                <Column header="NET KASA" body={(r) => {
                                    const net = (r.collectionTotal || 0) - (r.expenseTotal || 0);
                                    return <span className={`font-black ${net >= 0 ? 'text-violet-600 bg-violet-50' : 'text-rose-600 bg-rose-50'} px-2 py-0.5 rounded`}>{fmtMoney(net)}</span>
                                }} sortable style={{ textAlign: 'right' }} headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 text-right" />
                            </DataTable>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
